import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import modalStyles from '@/features/application/styles/modals.module.css'
import { showApiError } from '@/lib/utils/notifications'
import { getCronDetail } from '../services/jobsService'
import { createCronFormSchema, type CronFormValues } from '../types/schemas'
import type { CronChangeLog, CronEditModalProps, CronSubmitPayload } from '../types'
import { formatCronChangeLogCreatedAt, isFrequencyConfigurableCron } from '../utils/cronFormatters'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'

function inputClass(hasError: boolean): string {
  const base =
    'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]'
  return hasError ? `${base} border-red-500` : `${base} border-slate-300`
}

const WEEKDAYS: { value: string; label: string }[] = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
]

export function CronEditModal({
  isOpen,
  cronJob,
  submitting,
  onClose,
  onSubmit,
}: CronEditModalProps) {
  const isEdit = cronJob !== null
  const frequencyConfigurable = isEdit && isFrequencyConfigurableCron(cronJob?.cron_id)
  const showWeeklyField = isEdit && (frequencyConfigurable || Boolean(cronJob?.weekly_cron))
  const showTimeField = isEdit && Boolean(cronJob?.cron_time)

  const [changeLog, setChangeLog] = useState<CronChangeLog[]>([])

  const formSchema = useMemo(() => createCronFormSchema(isEdit), [isEdit])

  const {
    register,
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { errors },
  } = useForm<CronFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      description: '',
      weekly_cron: '',
      time: '',
      frequency: 'daily',
      name: '',
      cron_id: '',
      status: 'INACTIVE',
    },
  })
  const frequency = watch('frequency')

  useEffect(() => {
    if (!isOpen) return
    // Mirrors Angular ngOnInit: only patch the fields that exist on the
    // cron detail; leave id/name blank in edit mode (Angular HTML does
    // not render those inputs in edit mode at all).
    reset({
      description: cronJob?.cron_description ?? '',
      weekly_cron: cronJob?.weekly_cron || '1',
      time: cronJob?.cron_time ?? '',
      frequency: cronJob?.weekly_cron ? 'weekly' : 'daily',
      name: '',
      cron_id: '',
      status: cronJob?.cron_status ?? 'INACTIVE',
    })
  }, [isOpen, cronJob, reset])

  // Angular fetches the change log via GET /api/cron/{id} immediately
  // when the modal opens for an existing cron job.
  useEffect(() => {
    if (!isOpen || !cronJob?.cron_id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setChangeLog([])
      return
    }
    let cancelled = false
    void getCronDetail(cronJob.cron_id)
      .then((data) => {
        if (!cancelled) setChangeLog(data)
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, cronJob])

  const internalSubmit = (values: CronFormValues) => {
    // Angular's submitForm() forces status to the existing cron_status
    // when editing — the user can only flip status via the Active/Inactive
    // toggle on the list page, never inside the edit modal.
    const payload: CronSubmitPayload = isEdit
      ? {
          description: values.description.trim(),
          status: cronJob?.cron_status ?? 'INACTIVE',
          ...(frequencyConfigurable
            ? {
                weekly_cron: values.frequency === 'weekly' ? values.weekly_cron || '1' : '',
                ...(values.time ? { time: values.time } : {}),
              }
            : {
                ...(showWeeklyField && values.weekly_cron
                  ? { weekly_cron: values.weekly_cron }
                  : {}),
                ...(showTimeField && values.time ? { time: values.time } : {}),
              }),
        }
      : {
          description: values.description.trim(),
          status: values.status,
          ...(values.cron_id.trim()
            ? { cron_id: values.cron_id.trim() }
            : {}),
          ...(values.name ? { name: values.name.trim() } : {}),
        }
    onSubmit(payload, cronJob?.cron_id)
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title={isEdit ? 'Edit Cron Details' : 'Add Cron Details'}
      onClose={onClose}
      maxWidth="max-w-800"
      bodyScrollOnly
      panelDataTestId="cron-edit-modal-panel"
      footer={
        <div
          className={cn(modalStyles.modalFooterActionGroup)}
          data-testid="cron-edit-modal-footer"
        >
          <button
            type="submit"
            form="cron-edit-form"
            disabled={submitting}
            className={cn(primaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="cron-edit-submit"
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="cron-edit-cancel"
          >
            Cancel
          </button>
        </div>
      }
    >
      <form
        id="cron-edit-form"
        key={isEdit ? `edit-${cronJob?.cron_id}` : 'add'}
        onSubmit={handleSubmit(internalSubmit)}
        className="space-y-4"
        noValidate
      >
        {isEdit && (frequencyConfigurable || showWeeklyField || showTimeField) && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {frequencyConfigurable && (
              <div>
                <label
                  htmlFor="cron-frequency"
                  className="mb-1 block text-sm font-medium text-slate-700"
                >
                  Frequency
                </label>
                <select
                  id="cron-frequency"
                  className={inputClass(false)}
                  {...register('frequency')}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
            )}
            {showWeeklyField && (!frequencyConfigurable || frequency === 'weekly') && (
              <div>
                <label
                  htmlFor="cron-weekly"
                  className="mb-1 block text-sm font-medium text-slate-700"
                >
                  Cron Day
                </label>
                <select
                  id="cron-weekly"
                  className={inputClass(Boolean(errors.weekly_cron))}
                  {...register('weekly_cron')}
                >
                  {WEEKDAYS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {showTimeField && (
              <div>
                <label
                  htmlFor="cron-time"
                  className="mb-1 block text-sm font-medium text-slate-700"
                >
                  Cron Time
                </label>
                <input
                  id="cron-time"
                  type="time"
                  className={inputClass(Boolean(errors.time))}
                  {...register('time')}
                />
                {errors.time && (
                  <p className={modalStyles.formFieldError} role="alert">
                    {errors.time.message}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {!isEdit && (
          <>
            <div>
              <label
                htmlFor="cron-id"
                className="mb-1 block text-sm font-medium text-slate-700"
              >
                Cron ID*
              </label>
              <input
                id="cron-id"
                type="text"
                inputMode="numeric"
                className={inputClass(Boolean(errors.cron_id))}
                aria-invalid={Boolean(errors.cron_id)}
                aria-describedby={errors.cron_id ? 'cron-id-error' : undefined}
                {...register('cron_id')}
              />
              {errors.cron_id && (
                <p id="cron-id-error" className={modalStyles.formFieldError} role="alert">
                  {errors.cron_id.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="cron-name"
                className="mb-1 block text-sm font-medium text-slate-700"
              >
                Cron Name*
              </label>
              <input
                id="cron-name"
                type="text"
                className={inputClass(Boolean(errors.name))}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'cron-name-error' : undefined}
                {...register('name')}
              />
              {errors.name && (
                <p id="cron-name-error" className={modalStyles.formFieldError} role="alert">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div>
              <span className="mb-1 block text-sm font-medium text-slate-700">
                Cron Status:
              </span>
              <div className="flex gap-4">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="radio" value="ACTIVE" {...register('status')} />
                  ACTIVE
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="radio" value="INACTIVE" {...register('status')} />
                  INACTIVE
                </label>
              </div>
            </div>
          </>
        )}

        <div>
          <span
            id="cron-description-label"
            className="mb-1 block text-sm font-medium text-slate-700"
          >
            Description*
          </span>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <CKEditor4
                id="cron-description"
                className={`min-h-[220px] [&_.ck-editor__editable]:min-h-[180px] ${
                  errors.description ? 'ring-2 ring-red-500 rounded-lg' : ''
                }`}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value)
                  void trigger('description')
                }}
                onBlur={() => void trigger('description')}
              />
            )}
          />
          {errors.description && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.description.message}
            </p>
          )}
        </div>
      </form>

      {isEdit && changeLog.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-sm font-semibold text-slate-700">Changes Logs</p>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left">Changes By</th>
                  <th className="px-3 py-2 text-left">Changes Data</th>
                  <th className="px-3 py-2 text-left">Created At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {changeLog.map((note, idx) => (
                  <tr key={`${idx}-${note.name}`}>
                    <td className="px-3 py-2 text-slate-700">{note.name}</td>
                    <td
                      className="px-3 py-2 text-slate-700"
                      // Mirrors Angular `innerHtml="{{note.changes_data}}"`
                      dangerouslySetInnerHTML={{ __html: note.changes_data ?? '' }}
                    />
                    <td className="px-3 py-2 text-slate-700">
                      {formatCronChangeLogCreatedAt(note.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </OnScreenModal>
  )
}
